import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// === CHANGED === Removed .js extension
import DetailsHeader from '../components/DetailsHeader';

// === CHANGED === Removed .js extension
import { db } from '../firebase';
import { collection, query, where, getDocs } from "firebase/firestore";

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';

// === CHANGED === Removed .js extension
import PermitTemplate from '../components/PermitTemplate';

// === NEW HELPER FUNCTION ===
// Formats "YYYY-MM-DDTHH:MM" into "DD/MM/YYYY HH:MM AM/PM"
const formatPermitDateTime = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    // Use en-GB locale to get DD/MM/YYYY format
    let formatted = date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    // Clean up the format: "31/10/2025, 10:24 am" -> "31/10/2025 10:24 AM"
    formatted = formatted.replace(',', '');
    formatted = formatted.replace(' am', 'AM').replace(' pm', 'PM');
    return formatted;
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return dateString; // Fallback
  }
};
// ==========================

// Label/Value component
const DetailItem = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    {/* === CHANGED === text-sm to text-base and text-gray-500 to text-text-main */}
    <dt className="text-base font-bold leading-normal text-text-main">{label}</dt>
    <dd className="text-base font-medium leading-normal text-text-main">{value || '-'}</dd>
  </div>
);

function ApplicationDetailsPage() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const pdfTemplateRef = useRef(null);
  
  const [appData, setAppData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrDataString, setQrDataString] = useState('');

  // Fetch the single document from Firestore
  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const q = query(collection(db, "applications"), where("mdroNo", "==", id));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docSnap = querySnapshot.docs[0];
          const data = docSnap.data();
          
          // === CHANGED ===
          // Use the new formatter to create the combined validity string
          const formattedData = {
            ...data,
            id: docSnap.id,
            mdpNoPart1: data.mdpNo.split(' ')[0] || data.mdpNo,
            mdpNoPart2: data.mdpNo.split(' ')[1] || '',
            leaseNoPart1: data.leaseNo.split(',')[0] || data.leaseNo,
            leaseNoPart2: data.leaseNo.split(',')[1] || data.leaseNo,
            buyerLine1: data.buyer.substring(0, 40),
            buyerLine2: data.buyer.substring(40),
            routeLine1: data.route.substring(0, 40),
            routeLine2: data.route.substring(40),
            // Create the combined validity string for the PDF and Web UI
            validity: `${formatPermitDateTime(data.validityStart)}-${formatPermitDateTime(data.validityEnd)}`
          };
          setAppData(formattedData);
          
          // --- Construct the specific QR Code string ---
          const qrString = [
            formattedData.mdroNo,
            (formattedData.mdpNoPart1 || '') + (formattedData.mdpNoPart2 || '').replace(/\s+/g, ''),
            formattedData.quantity + ' Tones',
            // Use the formatted start date for the QR string
            formatPermitDateTime(formattedData.validityStart), 
            (formattedData.vehicleInfo || '').split('/')[0] + ' ' + (formattedData.vehicleInfo || '').split('/')[2],
            formattedData.mineral,
            (formattedData.barcode || '') + (formattedData.leaseNoPart2 || '') + ' ' + (formattedData.destination || ''),
          ].join(' | ');
          
          setQrDataString(qrString);

          // Generate QR code
          QRCode.toDataURL(qrString, { errorCorrectionLevel: 'H', type: 'image/png' })
            .then(url => setQrCodeDataURL(url))
            .catch(err => console.error('Failed to generate QR code', err));
            
        } else {
          console.log("No such document with mdroNo:", id);
          alert("Application not found.");
          navigate('/dashboard');
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
      }
      setIsLoading(false);
    };

    fetchApplication();
  }, [id, navigate]);

  // PDF Generation Function (no change)
  const generatePDF = () => {
    if (!appData) return;
    setIsGenerating(true);
    const input = pdfTemplateRef.current;
    
    html2canvas(input, { width: 1150, height: 1627, scale: 2, useCORS: true, logging: false })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`MDRO_${appData.mdroNo}.pdf`);
        setIsGenerating(false);
      });
  };

  // Loading screen (no change)
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-text-color/70">Loading application details...</p>
      </div>
    );
  }

  // Not found screen (no change)
  if (!appData) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-text-color/70">Application not found.</p>
      </div>
    );
  }

  // Render the page
  return (
    <>
      <PermitTemplate 
        forwardedRef={pdfTemplateRef}
        appData={appData}
        qrCodeDataURL={qrCodeDataURL}
      />
      <div className="relative flex h-auto min-h-screen w-full flex-col group/design-root overflow-x-hidden">
        <DetailsHeader />
        <main className="flex flex-1 justify-center py-8 px-4 sm:px-6 lg:px-8">
          <div className="layout-content-container flex flex-col w-full max-w-5xl flex-1">
            <div className="flex flex-wrap justify-between items-center gap-4 px-4 py-2">
              <h2 className="text-text-main text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em] min-w-72">
                MDRO Application Details
              </h2>
            </div>
            
            <div className="mt-6">
              <div className="p-6 md:p-8">
                <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                  <DetailItem label="MDRO No" value={appData.mdroNo} />
                  <DetailItem label="Sr No" value={appData.srNo} />
                  <DetailItem label="MDP No" value={appData.mdpNo} />
                  <DetailItem label="Lease No" value={appData.leaseNo} />
                  <DetailItem label="Barcode" value={appData.barcode} />
                  <DetailItem label="Lease Name" value={appData.leaseName} />
                  <DetailItem label="Type of Land" value={appData.landType} />
                  <DetailItem label="DSC No / Name" value={appData.dscName} />
                  <DetailItem label="GST No" value={appData.gstNo} />
                  <DetailItem label="Taluk / District" value={appData.district} />
                  <DetailItem label="Place of Loading" value={appData.loadingPlace} />
                  <DetailItem label="MDRO Purpose" value={appData.purpose} />
                  <DetailItem label="Mineral / Grade" value={appData.mineral} />
                  <DetailItem label="Quantity (Tonnes)" value={appData.quantity} />
                  <DetailItem label="Total Amount Paid" value={appData.amount} />
                  <DetailItem label="Buyer" value={appData.buyer} />
                  <DetailItem label="Route" value={appData.route} />
                  <DetailItem label="Check Post" value={appData.checkPost} />
                  <DetailItem label="Destination" value={appData.destination} />
                  <div className="sm:col-span-2 lg:col-span-1">
                    <DetailItem label="Vehicle No/wheels/type/mobile No" value={appData.vehicleInfo} />
                  </div>
                  {/* === CHANGED === 
                    Now displays the single formatted 'validity' string
                  */}
                  <DetailItem label="Validity" value={appData.validity} />
                  <DetailItem label="Distance" value={appData.distance} />
                </dl>
              </div>
              <div className="px-6 py-4 flex justify-end">
                <div className="flex gap-3 flex-wrap justify-end">
                  <button
                    onClick={() => navigate(-1)} 
                    className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-gray-200 text-text-main text-sm font-bold leading-normal tracking-[0.015em] transition-colors hover:bg-gray-300"
                  >
                    <span className="truncate">Close</span>
                  </button>
                  <button 
                    onClick={generatePDF}
                    disabled={isGenerating}
                    className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-brand-primary text-white text-sm font-bold leading-normal tracking-[0.015em] transition-colors hover:bg-opacity-80 disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined mr-2 !text-xl">download</span>
                    <span className="truncate">
                      {isGenerating ? 'Generating...' : 'Download PDF'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default ApplicationDetailsPage;
